import { adminApi } from '@/config/axios.config';
import { exigirMotivoDeCancelacion, type MotivoDeCancelacionAdmin } from './motivo-de-cancelacion';
import type {
  Plan,
  Subscription,
  CreateSubscriptionDto,
  UpdateSubscriptionDto,
  PlanFilters,
  PlanStats,
  SubscriptionFilters,
} from '../types/admin.types';

// Re-export types for convenience
export type { Plan, PlanFilters, PlanStats, Subscription, SubscriptionFilters };

export interface PlanLimits {
  maxUsers: number;
  maxProperties: number;
  maxStorage: number;
  maxLeads: number;
  maxCampaigns: number;
  features: string[];
}

export interface PlansLimitsResponse {
  [planName: string]: PlanLimits;
}

/**
 * API service para gestión de planes y suscripciones
 */
export const plansApiService = {
  /**
   * Obtiene todos los planes
   */
  getAllPlans: async (filters?: PlanFilters): Promise<Plan[]> => {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.visibility) params.append('visibility', filters.visibility);
      if (filters?.search) params.append('search', filters.search);

      const response = await adminApi.get('/plans', { params });
      // El backend envuelve la respuesta en { success: true, data: [...] }
      const data = response.data?.data ?? response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      // Retornar array vacío en caso de error
      return [];
    }
  },

  /**
   * Obtiene un plan por ID
   */
  getPlan: async (id: string): Promise<Plan> => {
    const response = await adminApi.get(`/plans/${id}`);
    // El backend envuelve la respuesta en { success: true, data: {...} }
    return response.data?.data ?? response.data;
  },

  /**
   * Obtiene las estadísticas de planes
   */
  getPlanStats: async (): Promise<PlanStats> => {
    try {
      const response = await adminApi.get('/plans/stats');
      // El backend envuelve la respuesta en { success: true, data: {...} }
      return response.data?.data ?? response.data;
    } catch (error) {
      // Retornar estadísticas por defecto en caso de error
      return {
        total: 0,
        active: 0,
        inactive: 0,
        draft: 0,
        public: 0,
        private: 0,
      };
    }
  },

  /**
   * Obtiene los límites de todos los planes activos
   */
  getPlanLimits: async (): Promise<PlansLimitsResponse> => {
    const response = await adminApi.get('/plans/limits');
    // El backend envuelve la respuesta en { success: true, data: {...} }
    return response.data?.data ?? response.data;
  },

  /**
   * Crea un nuevo plan
   */
  createPlan: async (planData: Partial<Plan>): Promise<Plan> => {
    const response = await adminApi.post('/plans', planData);
    // El backend envuelve la respuesta en { success: true, data: {...} }
    return response.data?.data ?? response.data;
  },

  /**
   * Actualiza un plan
   */
  updatePlan: async (id: string, planData: Partial<Plan>): Promise<Plan> => {
    const response = await adminApi.patch(`/plans/${id}`, planData);
    // El backend envuelve la respuesta en { success: true, data: {...} }
    return response.data?.data ?? response.data;
  },

  /**
   * Elimina un plan
   */
  deletePlan: async (id: string): Promise<void> => {
    await adminApi.delete(`/plans/${id}`);
  },

  /**
   * Cambia el estado de un plan (toggle)
   */
  togglePlanStatus: async (id: string): Promise<Plan> => {
    const response = await adminApi.patch(`/plans/${id}/toggle-status`);
    // El backend envuelve la respuesta en { success: true, data: {...} }
    return response.data?.data ?? response.data;
  },

  /**
   * Duplica un plan
   */
  duplicatePlan: async (id: string): Promise<Plan> => {
    const response = await adminApi.post(`/plans/${id}/duplicate`);
    // El backend envuelve la respuesta en { success: true, data: {...} }
    return response.data?.data ?? response.data;
  },

  /**
   * Obtiene un plan por ID (alias para getPlan)
   */
  getPlanById: async (id: string): Promise<Plan> => {
    const response = await adminApi.get(`/plans/${id}`);
    // El backend envuelve la respuesta en { success: true, data: {...} }
    return response.data?.data ?? response.data;
  },

  // Subscriptions API
  subscriptions: {
    getSubscriptions: async (filters?: SubscriptionFilters): Promise<Subscription[]> => {
      try {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.planId) params.append('planId', filters.planId);
        if (filters?.search) params.append('search', filters.search);
        if (filters?.tenantId) params.append('tenantId', filters.tenantId);

        const response = await adminApi.get('/subscriptions', { params });
        // El backend envuelve la respuesta en { success: true, data: [...] }
        const data = response.data?.data ?? response.data;
        return Array.isArray(data) ? data : [];
      } catch (error) {
        return [];
      }
    },

    getSubscription: async (id: string): Promise<Subscription> => {
      const response = await adminApi.get(`/subscriptions/${id}`);
      // El backend envuelve la respuesta en { success: true, data: {...} }
      return response.data?.data ?? response.data;
    },

    createSubscription: async (data: CreateSubscriptionDto): Promise<Subscription> => {
      const response = await adminApi.post('/subscriptions', data);
      // El backend envuelve la respuesta en { success: true, data: {...} }
      return response.data?.data ?? response.data;
    },

    updateSubscription: async (id: string, data: UpdateSubscriptionDto): Promise<Subscription> => {
      const response = await adminApi.patch(`/subscriptions/${id}`, data);
      // El backend envuelve la respuesta en { success: true, data: {...} }
      return response.data?.data ?? response.data;
    },

    // #23510: la ruta del API es PATCH (esto era un POST, que da 404) y exige `motivo` (400 MOTIVO_REQUERIDO sin él).
    cancelSubscription: async (
      id: string,
      motivo: MotivoDeCancelacionAdmin,
      reason?: string
    ): Promise<Subscription> => {
      const response = await adminApi.patch(`/subscriptions/${id}/cancel`, {
        motivo: exigirMotivoDeCancelacion(motivo),
        reason,
      });
      // El backend envuelve la respuesta en { success: true, data: {...} }
      return response.data?.data ?? response.data;
    },

    renewSubscription: async (id: string): Promise<Subscription> => {
      const response = await adminApi.post(`/subscriptions/${id}/renew`);
      // El backend envuelve la respuesta en { success: true, data: {...} }
      return response.data?.data ?? response.data;
    },

    deleteSubscription: async (id: string): Promise<void> => {
      await adminApi.delete(`/subscriptions/${id}`);
    },
  },
};

export default plansApiService;
